/**
 * Фильтр темы «Голоса клиента» (#2229) между разделом и вкладками.
 *
 * - Отказ фильтра (403 `themes_disabled`, 400 `theme_filter_invalid`) любой
 *   вкладки снимает тему из адреса и говорит об этом в шапке раздела (§3):
 *   страница перечитывается без фильтра, а не рисует «не удалось загрузить» с
 *   вечным повтором. Решает раздел — вкладке нужен только сигнал.
 * - `prevComparable: false` в эхе гасит все сдвиги вкладки к прошлому периоду
 *   (§3): прошлое окно темы не считалось, его нули дали бы ложный рост.
 *   Значение по умолчанию — сравнимо: вне фильтра и на экране «Возражения»
 *   ничего не меняется.
 */
import { createContext, useContext, useEffect } from 'react';
import type { VoiceThemeFilterErrorCode } from '@aica/api-types/schemas';
import { themeFilterRefusalCode } from './voice-theme-filter-view-model';

/**
 * The refusal of the request narrowed by `themeCode`: the section lifts the
 * theme only while it is still the selected one — a late answer to a theme
 * already replaced is not a refusal of the new one.
 */
export const VoiceThemeRefusalContext = createContext<
  ((code: VoiceThemeFilterErrorCode, themeCode: string) => void) | null
>(null);

export const VoiceComparableContext = createContext(true);

/** Whether the tab may show changes against the previous period. */
export function useVoiceComparable(): boolean {
  return useContext(VoiceComparableContext);
}

/**
 * The filter's refusal among a tab's narrowed requests, handed to the section
 * once it is seen. Returned too: until the section lifts the theme, the tab
 * shows that it is loading, not that it failed.
 */
export function useThemeFilterRefusal(
  voiceTheme: string | undefined,
  errors: readonly unknown[]
): VoiceThemeFilterErrorCode | null {
  const code =
    voiceTheme === undefined
      ? null
      : (errors.map(themeFilterRefusalCode).find((found) => found !== null) ?? null);
  useReportThemeRefusal(voiceTheme, code);
  return code;
}

/**
 * Hands a refusal of the selected theme to the section once it is seen — the
 * server's (above), or the set's own word where no request is narrowed: «Site
 * and content» without questions asks nothing under a theme, and an archived
 * theme from a link would stay in the address (#2238).
 */
export function useReportThemeRefusal(
  voiceTheme: string | undefined,
  code: VoiceThemeFilterErrorCode | null
): void {
  const onRefused = useContext(VoiceThemeRefusalContext);
  useEffect(() => {
    if (code !== null && voiceTheme !== undefined) onRefused?.(code, voiceTheme);
  }, [code, voiceTheme, onRefused]);
}
